import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  Validators
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ButtonComponent,
  FormFieldComponent,
  FormGroupComponent,
  FormRowComponent,
  IconComponent,
  InputDirective,
  LabelComponent,
  NotificationService,
  OptionComponent,
  PlaceholderComponent,
  SelectComponent,
  SubHeaderComponent
} from '@pansa/ui';
import { AuthorizedEntityDTO, AuthorizedEntityForm, CreateAuthorizedEntityDTO, UpdateAuthorizedEntityDTO } from '../../models/authorized-entity.model';
import { AuthorizedEntityService } from '../../services/authorized-entity.service';
import { GroupTypeEnum } from '@app/features/groups/models/group.model';
import { KeycloakInfoService } from '@app/shared/services/keycloak-info.service';
import { FieldErrorComponent } from '@app/shared/components/field-error/field-error.component';

@Component({
  selector: 'rqa-add-authorized-entity',
  templateUrl: './add-authorized-entity.component.html',
  styleUrl: './add-authorized-entity.component.scss',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonComponent,
    IconComponent,
    OptionComponent,
    SelectComponent,
    PlaceholderComponent,
    LabelComponent,
    FormFieldComponent,
    FormRowComponent,
    InputDirective,
    FormGroupComponent,
    SubHeaderComponent,
    FieldErrorComponent
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true
})
export class AddAuthorizedEntityComponent {
  private readonly authorizedEntityService = inject(AuthorizedEntityService);
  private readonly keycloakInfoService = inject(KeycloakInfoService);
  private readonly router = inject(Router);
  private route = inject(ActivatedRoute);
  private fb = inject(FormBuilder);
  private location = inject(Location);
  private notificationService = inject(NotificationService)
  entityForm!: FormGroup<AuthorizedEntityForm>;
  groupTypeOptions: string[] = Object.values(GroupTypeEnum);
  editMode: boolean = false;
  groups = this.keycloakInfoService.groups;
  uuid: string = '';

  get phones(): UntypedFormArray {
    return this.entityForm.get('phones') as UntypedFormArray;
  }

  ngOnInit(): void {
    this.buildForm();
    const authorizedEntity = this.route.snapshot.data['authorizedEntity'];
    if (authorizedEntity) {
      this.uuid = authorizedEntity.uuid;
      this.setFormValues(authorizedEntity);
      this.editMode = true;
    }
  }

  onSubmit(): void {
    this.entityForm.markAllAsTouched();
    if (this.entityForm.invalid) {
      return;
    }
    if (this.editMode) {
      this.editAuthorizedEntity();
    } else {
      this.addAuthorizedEntity();
    }
  }

  addPhone(): void {
    if (this.phones.controls.length <= 5) {
      this.phones.push(
        this.fb.control('', [
          Validators.required,
          Validators.pattern('^[+]?[0-9 ]{7,19}$'),
          Validators.minLength(7),
          Validators.maxLength(19)
        ])
      );
    }
  }

  removePhone(phoneValue: number): void {
    this.phones.removeAt(phoneValue);
  }

  private editAuthorizedEntity(): void {
    const value = this.entityForm.getRawValue() as UpdateAuthorizedEntityDTO;
    
    this.authorizedEntityService.updateAuthorizedEntity(value, this.uuid).subscribe({
      next: (response) => {
        this.notificationService.success({ title: 'Sukces', subTitle: `Zaktualizowano podmiot ${value.name}` });
        this.router.navigate(['/authorized-entity']);
        this.authorizedEntityService.refreshAuthorizedEntity();
      },
      error: (error) => {
        console.error('Error updating group:', error);
      }
    }
    );
  }

  private addAuthorizedEntity(): void {
    const value = this.entityForm.getRawValue() as CreateAuthorizedEntityDTO;
    this.authorizedEntityService.addAuthorizedEntity(value).subscribe({
      next: (response) => {
        this.notificationService.success({ title: 'Sukces', subTitle: `Dodano podmiot ${value.name}` });
        this.router.navigate(['/authorized-entity']);
        this.authorizedEntityService.refreshAuthorizedEntity();
      },
      error: (error) => {
        console.error('Error updating group:', error);
      }
    });
  }

  onGoBack(): void {
    this.location.back();
  }

  private buildForm(): void {
    this.entityForm = this.fb.nonNullable.group({
      name: this.fb.nonNullable.control('', {
        validators: [
          Validators.required
        ]
      }),

      email: this.fb.nonNullable.control('', {
        validators: [Validators.email]
      }),

      groupValue: this.fb.nonNullable.control(this.groups()[0], {
        validators: [Validators.required]
      }),

      website: this.fb.nonNullable.control(''),
      street: this.fb.nonNullable.control(''),
      city: this.fb.nonNullable.control(''),
      phones: this.fb.nonNullable.array<FormControl<string>>([
        this.fb.nonNullable.control('', {
          validators: [
            Validators.required,
            Validators.pattern('^[+]?[0-9 ]{7,19}$'),
            Validators.minLength(7),
            Validators.maxLength(19)
          ]
        })
      ]),
      note: this.fb.nonNullable.control('')
    });
  }

  private setFormValues(authorizedEntity: AuthorizedEntityDTO): void {
    this.entityForm.patchValue({
      name: authorizedEntity.name,
      email: authorizedEntity.email,
      groupValue: authorizedEntity.groupValue,
      city: authorizedEntity?.city,
      street: authorizedEntity?.street,
      website: authorizedEntity?.website,
      note: authorizedEntity?.note
    });

    const phonesArray = this.entityForm.controls.phones;

    phonesArray.clear();

    authorizedEntity.phones.forEach((phone) => {
      phonesArray.push(
        this.fb.nonNullable.control(phone, {
          validators: [
            Validators.required,
            Validators.pattern('^[+]?[0-9 ]{7,19}$'),
            Validators.minLength(7),
            Validators.maxLength(19)
          ]
        })
      );
    });
  }
}
